
function Section({ title, children }) {
  return (
    <section className="pp-section">
      <h2 className="pp-section__title">{title}</h2>
      {children}
    </section>
  );
}

function Sub({ title }) {
  return <h3 className="pp-sub">{title}</h3>;
}

function Body({ children }) {
  return <p className="pp-body">{children}</p>;
}

function Bullet({ children }) {
  return (
    <div className="pp-bullet">
      <span className="pp-bullet__dot">•</span>
      <span className="pp-bullet__text">{children}</span>
    </div>
  );
}

function TableRow({ label, value, shade }) {
  return (
    <div className={`pp-table__row${shade ? " pp-table__row--shade" : ""}`}>
      <span className="pp-table__label">{label}</span>
      <span className="pp-table__value">{value}</span>
    </div>
  );
}

function ContactCard({ name, details }) {
  return (
    <div className="pp-contact">
      <p className="pp-contact__name">{name}</p>
      {details.map((d, i) => (
        <p key={i} className="pp-contact__detail">{d}</p>
      ))}
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div id="privacy-policy" className="pp-container">
      <div className="pp-content">
        <p className="pp-last-updated">Last updated: May 5, 2026</p>

        <Section title="1. Introduction">
          <Body>
            Dash Dynamic Private Limited is committed to protecting your personal data. This Privacy
            Policy explains how we collect, use, store, share, and protect information about you
            ("User", "you") when you use the Dash Dynamic mobile application ("App") and related
            services ("Service").
          </Body>
          <Body>
            This policy is compliant with the Digital Personal Data Protection Act, 2023 (DPDP
            Act), the Information Technology (Reasonable Security Practices and Procedures and
            Sensitive Personal Data or Information) Rules, 2011, and other applicable Indian laws.
          </Body>
          <Body>
            By using the App, you consent to the data practices described in this Privacy Policy.
          </Body>
        </Section>

        <Section title="2. Information We Collect">
          <Sub title="2.1 Information You Provide Directly" />
          <div className="pp-table">
            <TableRow label="Identity" value="Full name" shade={false} />
            <TableRow label="Contact" value="Mobile phone number, email address" shade={true} />
            <TableRow label="Authentication" value="Password (hashed), OTP verification" shade={false} />
            <TableRow label="Vehicle" value="Vehicle brand, model, type" shade={true} />
            <TableRow
              label="Payment"
              value="Wallet top-up amount, selected payment method (we do not store full payment card/UPI credentials)"
              shade={false}
            />
          </div>

          <Sub title="2.2 Information Generated Through Your Use" />
          <div className="pp-table">
            <TableRow
              label="Charging sessions"
              value="Session ID, charger ID, start/end timestamps, energy consumed (Wh/kWh), duration"
              shade={false}
            />
            <TableRow
              label="Real-time telemetry"
              value="Voltage (V), current (A), battery state of charge (%), instantaneous power (W), charge uptime"
              shade={true}
            />
            <TableRow
              label="Transaction records"
              value="Wallet balance, deduction history, top-up history, charging costs"
              shade={false}
            />
            <TableRow
              label="Account activity"
              value="Login events, logout events, session history"
              shade={true}
            />
          </div>

          <Sub title="2.3 Information Collected Automatically" />
          <div className="pp-table">
            <TableRow
              label="Device info"
              value="Device platform (Android/iOS), app version"
              shade={false}
            />
            <TableRow
              label="Bluetooth"
              value="BLE device ID of connected charging station (hardware identifier only)"
              shade={true}
            />
            <TableRow
              label="Network"
              value="Whether the device is connected to the internet (used to determine charging mode)"
              shade={false}
            />
            <TableRow
              label="Usage data"
              value="App interactions, error logs, crash reports"
              shade={true}
            />
          </div>

          <Sub title="2.4 What We Do Not Collect" />
          <Bullet>Precise GPS location or location history</Bullet>
          <Bullet>Contacts, photos, or files on your device</Bullet>
          <Bullet>Data from other apps on your device</Bullet>
          <Bullet>Biometric data</Bullet>
        </Section>

        <Section title="3. How We Use Your Information">
          <Body>We process your personal data only for the following lawful purposes:</Body>
          <div className="pp-table">
            <TableRow label="Account creation & management" value="Contract performance; Consent" shade={false} />
            <TableRow label="Providing charging sessions" value="Contract performance" shade={true} />
            <TableRow label="Wallet & payment processing" value="Contract performance; Legal obligation" shade={false} />
            <TableRow label="Sending OTPs & notifications" value="Contract performance; Consent" shade={true} />
            <TableRow label="Displaying charging data" value="Contract performance" shade={false} />
            <TableRow label="Customer support" value="Legitimate interest; Legal obligation" shade={true} />
            <TableRow label="Improving the App" value="Legitimate interest" shade={false} />
            <TableRow label="Fraud detection & security" value="Legitimate interest; Legal obligation" shade={true} />
            <TableRow label="Compliance with law" value="Legal obligation" shade={false} />
          </div>
          <Body>
            We do not use your personal data for automated decision-making that has a significant
            legal or similarly significant effect on you without your explicit consent.
          </Body>
        </Section>

        <Section title="4. Data Sharing and Disclosure">
          <Body>
            We do not sell, rent, or trade your personal data to third parties. We may share your
            data only in the following limited circumstances:
          </Body>
          <Sub title="4.1 Service Providers" />
          <Body>
            We share data with trusted third-party service providers who process data on our behalf,
            including:
          </Body>
          <Bullet>
            Backend infrastructure — hosting the API that stores user accounts and session data
          </Bullet>
          <Bullet>
            Payment processors — facilitating wallet top-ups (PhonePe, Google Pay, Paytm). These
            providers are not given access to your charging or telemetry data
          </Bullet>
          <Bullet>Communication services — for OTP delivery and customer notifications</Bullet>

          <Sub title="4.2 Legal Requirements" />
          <Body>
            We may disclose your data if required to do so by law, court order, or governmental
            authority, or to protect the rights, property, or safety of Dash Dynamic, our users, or
            the public.
          </Body>

          <Sub title="4.3 Business Transfers" />
          <Body>
            In the event of a merger, acquisition, or sale of assets, your data may be transferred
            to the successor entity, subject to the same privacy protections described in this
            policy.
          </Body>

          <Sub title="4.4 Aggregated and Anonymised Data" />
          <Body>
            We may share aggregated, anonymised data (e.g., overall charging usage statistics) that
            cannot reasonably be used to identify you.
          </Body>
        </Section>

        <Section title="5. Data Storage and Security">
          <Sub title="5.1 Storage and Cross-Border Transfer" />
          <Body>
            Your personal data is primarily stored on servers located in India. We may transfer or
            store your data with service providers located outside India, in accordance with the
            Digital Personal Data Protection Act, 2023. Under the DPDP Act, we may transfer personal
            data to any country or territory outside India, except where the Central Government has,
            by notification, restricted such transfer. We do not transfer your personal data to any
            jurisdiction so restricted. Authentication tokens are stored locally on your device in
            encrypted secure storage (expo-secure-store).
          </Body>

          <Sub title="5.2 Security Measures" />
          <Bullet>HTTPS (TLS) encryption for all data transmitted between the App and our servers</Bullet>
          <Bullet>JWT-based authentication with refresh token rotation</Bullet>
          <Bullet>Encrypted local storage of authentication credentials on your device</Bullet>
          <Bullet>Access controls limiting employee access to personal data on a need-to-know basis</Bullet>

          <Sub title="5.3 Data Breach Notification" />
          <Body>
            In the event of a personal data breach that is likely to result in harm to you, we will
            notify the Data Protection Board of India and affected users as required under the
            Digital Personal Data Protection Act, 2023. Notification will describe the nature of the
            breach, the personal data likely affected, and the measures taken or proposed to mitigate
            any resulting harm.
          </Body>

          <Sub title="5.4 Limitations" />
          <Body>
            No method of transmission or storage is completely secure. While we take reasonable
            steps to protect your data, we cannot guarantee absolute security. Report any suspected
            security breach to security@dashdynamic.in.
          </Body>
        </Section>

        <Section title="6. Data Retention">
          <div className="pp-table">
            <TableRow label="Account data" value="Duration of account + 2 years after deletion" shade={false} />
            <TableRow label="Charging session records" value="3 years from session date" shade={true} />
            <TableRow label="Transaction records" value="7 years (financial regulations)" shade={false} />
            <TableRow label="Authentication logs" value="90 days" shade={true} />
            <TableRow label="Crash and error logs" value="30 days" shade={false} />
          </div>
          <Body>
            After the applicable retention period, data is securely deleted or anonymised.
          </Body>
        </Section>

        <Section title="7. Your Rights">
          <Body>
            Under the Digital Personal Data Protection Act, 2023, you have the following rights:
          </Body>
          <Bullet>Access: Request a copy of the personal data we hold about you</Bullet>
          <Bullet>Correction: Request correction of inaccurate or incomplete data</Bullet>
          <Bullet>
            Erasure: Request deletion of your personal data, subject to legal retention obligations
          </Bullet>
          <Bullet>Withdrawal of Consent: Withdraw consent at any time</Bullet>
          <Bullet>
            Nomination: Nominate another individual to exercise your rights under this policy in the
            event of your death or incapacity
          </Bullet>
          <Bullet>Grievance Redressal: Lodge a complaint with our Grievance Officer (Section 11)</Bullet>
          <Body>
            To exercise any of these rights, contact us at privacy@dashdynamic.in. We will respond
            within 30 days of receiving your request.
          </Body>
        </Section>

        <Section title="8. Bluetooth Permissions">
          <Body>
            The App requires Bluetooth Low Energy (BLE) permission to connect to and communicate
            with Dash Dynamic charging stations. Bluetooth is used solely to:
          </Body>
          <Bullet>Discover Dash Dynamic charging hardware in your immediate vicinity</Bullet>
          <Bullet>Establish a direct encrypted connection to the charging station</Bullet>
          <Bullet>
            Send start/stop commands and receive real-time telemetry from the connected charger
          </Bullet>
          <Body>
            We do not use Bluetooth to scan for or collect data from other devices, track your
            location, or share data with advertisers.
          </Body>
        </Section>

        <Section title="9. Notifications">
          <Body>
            We may send you push notifications and SMS/WhatsApp messages for:
          </Body>
          <Bullet>OTP delivery for authentication</Bullet>
          <Bullet>
            Charging session status updates (session started, completed, low balance)
          </Bullet>
          <Bullet>Critical account alerts (e.g., unusual activity)</Bullet>
          <Bullet>Service announcements and updates</Bullet>
          <Body>
            You may manage push notification preferences in your device settings. Disabling
            notifications may affect your ability to receive critical account alerts.
          </Body>
        </Section>

        <Section title="10. Children's Privacy">
          <Body>
            The App is not directed at or intended for use by individuals under the age of 18. We do
            not knowingly collect personal data from minors. If you believe we have inadvertently
            collected data from a minor, contact us immediately at privacy@dashdynamic.in.
          </Body>
        </Section>

        <Section title="11. Grievance Officer">
          <Body>
            In accordance with the Information Technology Act, 2000 and the Digital Personal Data
            Protection Act, 2023, we have appointed the following Grievance Officer:
          </Body>
          <ContactCard
            name="Robin Singh"
            details={[
              "robin.singh@dashdynamic.in",
              "2-B-5C Research and Innovation Park, IIT Flyover, Hauzkhas, New Delhi, India",
            ]}
          />
          <Body>
            Complaints will be acknowledged within 48 hours and resolved within 30 days of receipt.
          </Body>
        </Section>

        <Section title="12. Changes to This Privacy Policy">
          <Body>
            We may update this Privacy Policy from time to time. When we make material changes, we
            will notify you via the App or your registered email address. Continued use of the App
            after the effective date of any changes constitutes your acceptance of the revised
            policy.
          </Body>
        </Section>

        <Section title="13. Contact Us">
          <ContactCard
            name="Dash Dynamic Pvt. Ltd."
            details={[
              "privacy@dashdynamic.in",
              "support@dashdynamic.in",
              "www.dashdynamic.in",
            ]}
          />
        </Section>
      </div>
    </div>
  );
}